let Gist = null;

const getGist = () => {
  if (!Gist) {
    Gist = window.Gist || null;
  }
  return Gist;
};

export const identifyUser = (userId, userData = {}) => {
  if (window.cioanalytics) {
    try {
      window.cioanalytics.identify(userId, userData);

      const gist = getGist();
      if (gist && typeof gist.setUserToken === 'function') {
        gist.setUserToken(userId);
      }

      localStorage.setItem(
        'customerio_user',
        JSON.stringify({
          userId,
          userData,
          identifiedAt: new Date().toISOString(),
        }),
      );
    } catch (error) {
      console.error('Failed to identify user in Customer.io:', error);
    }
  } else {
    console.warn('Customer.io analytics not initialized');
  }
};

export const trackEvent = (eventName, properties = {}) => {
  if (window.cioanalytics) {
    try {
      const promise = window.cioanalytics.track(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
      return promise;
    } catch (error) {
      console.error('Failed to track event in Customer.io:', error);
      return Promise.reject(error);
    }
  } else {
    console.warn('Customer.io analytics not available');
    return Promise.reject(new Error('Customer.io analytics not available'));
  }
};

export const resetUser = () => {
  if (window.cioanalytics) {
    try {
      window.cioanalytics.reset();

      const gist = getGist();
      if (gist && typeof gist.clearUserToken === 'function') {
        gist.clearUserToken();
      }

      localStorage.removeItem('customerio_user');
    } catch (error) {
      console.error('Failed to reset user in Customer.io:', error);
    }
  } else {
    console.warn('Customer.io analytics not initialized');
  }
};

export const retryUserIdentification = () => {
  try {
    const storedUser = localStorage.getItem('customerio_user');
    if (storedUser && getGist()) {
      const userData = JSON.parse(storedUser);

      const gist = getGist();
      if (gist && typeof gist.setUserToken === 'function') {
        gist.setUserToken(userData.userId);
      }
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error retrying Customer.io user identification:', error);
    return false;
  }
};

export const checkCustomerIOStatus = () => {
  try {
    const gist = getGist();
    const currentMessages = gist && gist.currentMessages ? gist.currentMessages : [];

    return {
      gistInitialized: !!getGist(),
      userIdentified: !!window.cioanalytics,
      currentMessages,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Failed to check Customer.io status:', error);
    return { error: error.message };
  }
};

export const restoreUserIdentification = () => {
  try {
    const storedUser = localStorage.getItem('customerio_user');
    if (storedUser && getGist()) {
      const userData = JSON.parse(storedUser);

      const gist = getGist();
      if (gist && typeof gist.setUserToken === 'function') {
        gist.setUserToken(userData.userId);
      }
      console.log('Customer.io user identification restored successfully');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error restoring Customer.io user identification:', error);
    return false;
  }
};

export const forceCheckCustomerIOMessages = () => {
  try {
    const gist = getGist();
    if (gist && typeof gist.showMessage === 'function') {
      gist.showMessage();
    }
  } catch (error) {
    console.error('Failed to check Customer.io messages:', error);
  }
};

export const dismissCustomerIOMessage = () => {
  try {
    const gist = getGist();
    if (gist && typeof gist.dismissMessage === 'function') {
      gist.dismissMessage();
      console.log('✅ Customer.io message dismissed');
    } else {
      console.warn('⚠️ Gist dismissMessage method not available');
    }
  } catch (error) {
    console.error('Failed to dismiss Customer.io message:', error);
  }
};

export const triggerBroadcast = async (broadcastId, eventData = {}) => {
  try {
    const tokens = localStorage.getItem('tokens');
    if (!tokens) {
      console.warn('⚠️ No tokens available for broadcast trigger');
      return;
    }

    const { idToken } = JSON.parse(tokens);
    if (!idToken) {
      console.warn('⚠️ No ID token available for broadcast trigger');
      return;
    }

    const response = await fetch('/api/user/trigger-customerio-broadcast', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        broadcast_id: broadcastId,
        event_data: {
          ...eventData,
          timestamp: new Date().toISOString(),
        },
      }),
    });

    if (response.ok) {
      console.log(`✅ Customer.io broadcast ${broadcastId} triggered successfully`);
      await response.json();
    }
    const errorData = await response.text();
    console.error(`❌ Failed to trigger broadcast ${broadcastId}:`, errorData);
    throw new Error(`Broadcast trigger failed: ${response.status} ${response.statusText}`);
  } catch (error) {
    console.error('Failed to trigger Customer.io broadcast:', error);
    throw error;
  }
};

export const getCustomerIOMessageStatus = () => {
  try {
    const gist = getGist();
    const currentMessages = gist && gist.currentMessages ? gist.currentMessages : [];
    return {
      gistInitialized: !!getGist(),
      userIdentified: !!window.cioanalytics,
      currentMessages,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Failed to get Customer.io message status:', error);
    return { error: error.message };
  }
};

export const logCustomerIOStatus = () => {
  // Function kept for compatibility but logs removed
};
